import React, { useState, useEffect } from 'react';
import { Line, Bar} from 'react-chartjs-2';
import axios from "axios";
import {API_URL} from "../../App";


const MetricDisplay = ({ botInfo, type, title}) => {
  const [metricsList, setMetricsList] = useState([]); // List of available metrics
  const [selectedMetric, setSelectedMetric] = useState(''); // Currently selected metric
  const [metricData, setMetricData] = useState(null); // Data for the selected metric
  const [timestamps, setTimestamps] = useState(null);

  useEffect(() => {
    // Fetch the list of available metrics from the database
    const fetchMetrics = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/available-${type}-metrics/${botInfo.bot.id}`);
        setMetricsList(response.data.sort());
        if (response.data.length > 0) {
          setSelectedMetric(response.data[0]); // Set the first metric as default
          const response2 = await axios.get(`${API_URL}/api/bot-metric/${botInfo.bot.id}/timestamps`);
          setTimestamps(response2.data);
        }
      } catch (error) {
        //console.error("Failed to fetch timestamps", error);
      }
    };
    fetchMetrics();
  }, []);

  useEffect(() => {
    // Fetch data for the selected metric
    if (selectedMetric) {
      const fetchData = async () => {
        try {
          const response = await axios.get(`${API_URL}/api/bot-metric/${botInfo.bot.id}/${type}/${encodeURIComponent(selectedMetric)}`);
          //console.log(response);
          setMetricData(response.data);
          
        } catch (error) {
          //console.error("Failed to fetch bot from tag");
        }
      };
      fetchData();
    }
  }, [selectedMetric]);

  // Function to generate a unique hue for the metric
  const generateColorFromIndex = (index) => {
    const hue = (index * 60) % 360; // Change the multiplier for wider hue range
    return hue;
  };


  // Get the color for the current selected metric
  const metricIndex = metricsList.indexOf(selectedMetric);
  const curveColor = generateColorFromIndex(metricIndex);

  // Chart configuration
  const LineChart = ({label, data, timestamps, color}) => {
    const dataset = {
      labels: timestamps, // X-axis: timestamps
      datasets: [
        {
          label: label.replace("$", "/"),
          data: data,
          borderColor: `hsl(${color}, 70%, 50%, 50%)`,
          backgroundColor: `hsl(${color}, 90%, 50%, 80%)`, // Add transparency
          fill: false,
          tension: 0.4, // Smooth curves
        },
      ],
    }
    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
          labels: {
            color: 'white',
          },
        },
      },
      scales: {
        x: {
          type: 'time',
          time: {
            unit: 'hour',
          },
          grid: {
            color: 'rgba(255, 255, 255, 0.1)',
          },
          ticks: {
            color: 'white',
          },
          title: {
            display: true,
            text: `${label.replace("$", "/")} over time (last 24h)`,
            color: 'white',
            font: {
              size: 16,
              family: "FolkProH",
            },
          },
        },
        y: {
          grid: {
            color: 'rgba(255, 255, 255, 0.1)',
          },
          ticks: {
            color: 'white',
          },
        },
      },
    };

    return <Line data={dataset} options={chartOptions} />
  };

  const BarChart = ({label, data, color}) => {
    // Convert dictionary to Chart.js format
    const labels = Object.keys(data);
    const values = Object.values(data);
  
    const dataset = {
      labels: labels,
      datasets: [
        {
          label: label.replace("$", "/"),
          data: values,
          borderColor: `hsl(${color}, 90%, 50%, 80%)`,
          backgroundColor:`hsl(${color}, 70%, 50%, 50%)`,
          borderWidth: 2,
          borderRadius: 8, // Rounded bars
          fill:false,
        },
      ],
    };
  
    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
          labels: {
            color: 'white',
          },
        },
      },
      scales: {
        x: {
          ticks: {
            color: 'white',
            maxRotation: 45,
            minRotation: 45,
            font: {
              size: 8,
              family: "FolkProH",
            },
          },
          title: {
            display: true,
            text: `${selectedMetric.replace("$", "/")} over time (last 24h)`,
            color: 'white',
            font: {
              size: 16,
              family: "FolkProH",
            },
          },
        },
        y: {
          grid: {
            color: 'rgba(255, 255, 255, 0.1)',
          },
          ticks: {
            color: 'white',
          },
        },
      },
    };
  
    return <Bar data={dataset} options={chartOptions} />;
  };

  // Display a message if no data is available
  if (!metricData || !timestamps) {
    return (
      <div className="p-6 rounded-lg shadow-lg space-y-8">
        <p className="text-white">Loading data...</p>
      </div>
    );
  }
  if (timestamps.length === 0) {
    return (
      <div className="p-6 rounded-lg shadow-lg space-y-8">
        <p className="text-white">No data.</p>
      </div>
    );
  }

  return (
    <div className="py-4 px-8">
      <h2 className="text-2xl font-bold pb-4 mb-4 text-center border-b border-gray-700">{title}</h2>

      {/* Dropdown for selecting a metric */}
      <div className="mb-4">
        <select
          id="metric-select"
          value={selectedMetric}
          onChange={(e) => setSelectedMetric(e.target.value)}
          className="bg-gray-800 text-white rounded-lg p-2"
        >
          {metricsList.map((metric) => (
            <option key={metric} value={metric}>
              {metric.replace("$", "/")}
            </option>
          ))}
        </select>
      </div>

      {/* Chart for the selected metric */}
      <div className="w-full h-80">
        {Array.isArray(metricData) ?
          <LineChart label={selectedMetric} data={metricData} timestamps={timestamps.map((ts) => new Date(ts))} color={curveColor} />
                 :
          <BarChart label={selectedMetric} data={metricData} color={curveColor} />
         }
      </div>
    </div>
  );
};

export default MetricDisplay;
